import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";
import QuoteSla from "@/components/QuoteSla";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Pricing() {
  const [rows, setRows] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/pricing").then((r) => setRows(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="pricing-page" className="pt-24 sm:pt-28">
      <Seo title="Get a Quote" description="Every project at Arsenal Artists is quoted individually — logos, graphics, branding, image editing, video and custom work. We reply within 2 business days." path="/pricing" image="/og-quote.png" />
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <Eyebrow>GET A QUOTE</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-balance">
          Pay for the work. <span className="text-arsenal">Nothing else.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Everything is quoted per project — from a single avatar to a full brand package — so you only ever pay for the scope you actually need.
        </p>
        <QuoteSla className="mt-6" />
      </header>

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="border border-white/10" data-testid="pricing-table">
          {rows.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.03}>
              <div className={`flex items-center justify-between gap-4 px-6 sm:px-8 py-5 ${i % 2 ? "bg-ink-surface/60" : ""} border-b border-white/5 last:border-0`} data-testid={`pricing-row-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div>
                  <p className="font-display font-bold text-white">{r.label}</p>
                  <p className="text-sm text-slate-500">{r.note}</p>
                </div>
                {r.mode === "quote" ? (
                  <Link
                    to="/start-a-project"
                    data-testid={`pricing-quote-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="clip-tag shrink-0 border border-arsenal/60 text-arsenal hover:bg-arsenal hover:text-white px-4 py-2 font-mono text-[11px] tracking-[0.15em] transition-colors"
                  >
                    REQUEST A QUOTE
                  </Link>
                ) : (
                  <p className="shrink-0 font-mono text-sm text-slate-300">
                    FROM <span className="text-arsenal font-bold">${r.price_from ?? "—"}</span>
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-5 text-sm text-slate-500" data-testid="pricing-note">
          Everything is quoted per project — tell us what you need and we'll come back with a clear price before any work begins. Your quote is always confirmed with you first.
        </p>
      </div>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <Reveal>
          <div className="border border-arsenal/30 bg-gradient-to-br from-arsenal/10 to-transparent p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6" data-testid="pricing-subscription-teaser">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Need creative work every month?</h2>
              <p className="mt-2 text-slate-400">A subscription beats re-quoting every job.</p>
            </div>
            <Link to="/subscriptions" data-testid="pricing-subscriptions-cta" className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-7 py-3.5 transition-colors">
              SEE SUBSCRIPTIONS <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {faqs.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-24" data-testid="pricing-faq">
          <Reveal>
            <Eyebrow>COMMON QUESTIONS</Eyebrow>
            <Accordion type="single" collapsible>
              {faqs.map((f, i) => (
                <AccordionItem key={f.id} value={`f-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`pricing-faq-q-${i}`} className="text-left font-display font-semibold text-white hover:text-arsenal">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Link to="/faq" data-testid="pricing-faq-all" className="mt-6 inline-block font-mono text-xs tracking-[0.2em] text-arsenal hover:text-arsenal-bright">
              ALL QUESTIONS →
            </Link>
          </Reveal>
        </section>
      )}
    </div>
  );
}
